import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CarCard from "@/components/CarCard";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Search, Loader2, X, Car, Users, SlidersHorizontal } from "lucide-react";

const ISLANDS = ["Grenada", "St. Vincent and the Grenadines", "Barbados", "Saint Lucia", "Turks & Caicos", "Bahamas", "Jamaica"];

export default function Cars() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const carType = params.get("car_type") || "";
  const island = params.get("island") || "";
  const minCapacity = params.get("min_capacity") || "";
  const maxPrice = params.get("max_price") || "";
  const sort = params.get("sort") || "recommended";

  useEffect(() => {
    api.get("/cars/meta").then((r) => setCarTypes(r.data.car_types)).catch(() => {});
  }, []);

  // Re-engagement: track authenticated car searches to trigger the 24h promo nudge.
  useEffect(() => {
    if (user) api.post("/cars/track-search").catch(() => {});
  }, [user]);

  const fetchCars = useCallback(() => {
    setLoading(true);
    const p = {};
    if (carType) p.car_type = carType;
    if (island) p.island = island;
    if (minCapacity) p.min_capacity = minCapacity;
    if (maxPrice) p.max_price = maxPrice;
    if (params.get("q")) p.q = params.get("q");
    if (sort) p.sort = sort;
    api.get("/cars", { params: p })
      .then((r) => setCars(r.data))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, [carType, island, minCapacity, maxPrice, sort, params]);

  useEffect(() => { fetchCars(); }, [fetchCars]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };
  const submitSearch = (e) => { e.preventDefault(); update("q", q); };
  const clearAll = () => { setQ(""); setParams(new URLSearchParams()); };
  const activeFilters = [carType, island, minCapacity, maxPrice, params.get("q")].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#060810]">
      <Seo title="Rent a Car in the Caribbean | Carivisor" description="Book cars, SUVs, jeeps and vans across the Caribbean. Filter by type, passenger capacity and daily price. Airport & hotel delivery available." path="/cars" />

      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-10 pb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Car className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">Car Rentals</h1>
            <p className="text-slate-400 text-sm mt-0.5">{loading ? "Searching…" : `${cars.length} vehicles available`}</p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="glass rounded-2xl p-4 mt-6 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <form onSubmit={submitSearch} className="flex-1 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
            <Search className="w-5 h-5 text-cyan-400 shrink-0" />
            <input
              data-testid="cars-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search make, model or island…"
              className="bg-transparent text-white text-sm w-full outline-none placeholder:text-slate-500"
            />
          </form>

          <select data-testid="cars-type-select" value={carType} onChange={(e) => update("car_type", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="">All car types</option>
            {carTypes.map((c) => <option key={c} value={c} className="bg-[#0B0E1B]">{c}</option>)}
          </select>

          <select data-testid="cars-capacity-select" value={minCapacity} onChange={(e) => update("min_capacity", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="">Any capacity</option>
            {[2, 4, 5, 7, 9].map((n) => <option key={n} value={n} className="bg-[#0B0E1B]">{n}+ passengers</option>)}
          </select>

          <select data-testid="cars-island-select" value={island} onChange={(e) => update("island", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="">All islands</option>
            {ISLANDS.map((i) => <option key={i} value={i} className="bg-[#0B0E1B]">{i}</option>)}
          </select>

          <select data-testid="cars-sort-select" value={sort} onChange={(e) => update("sort", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="recommended">Recommended</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="capacity">Most seats</option>
          </select>

          {activeFilters > 0 && (
            <button onClick={clearAll} data-testid="cars-clear-filters" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#FF5E7E] px-3 py-2.5">
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>

        {/* Price per day quick chips */}
        <div className="flex items-center gap-3 mt-4 overflow-x-auto no-scrollbar">
          <span className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0"><SlidersHorizontal className="w-3.5 h-3.5" /> Max / day:</span>
          {[50, 75, 100, 150].map((price) => (
            <button key={price} onClick={() => update("max_price", String(maxPrice) === String(price) ? "" : String(price))}
              data-testid={`cars-price-chip-${price}`}
              className={`text-xs px-4 py-1.5 rounded-full border shrink-0 transition-colors ${
                String(maxPrice) === String(price) ? "btn-neon border-transparent font-semibold" : "glass border-[#1D2640] text-slate-300 hover:border-cyan-400/40"
              }`}>
              ${price}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-16">
        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : cars.length === 0 ? (
          <div className="text-center py-32" data-testid="cars-empty">
            <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="font-heading text-xl text-white mb-2">No vehicles found</p>
            <p className="text-slate-400 text-sm mb-6">Try widening your filters, or check back soon.</p>
            <button onClick={clearAll} className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm">Reset filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="cars-grid">
            {cars.map((c) => <CarCard key={c.id} car={c} />)}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
